import { useQueryClient } from 'react-query';
import useCanvasMutation from '../useCanvasMutation';
import invalidateCanvasLists from '../helpers/invalidateCanvasLists';
import type {
    CreateCanvasBody,
    CreateCanvasInput,
    CreateCanvasResponse,
    SerializedCanvas,
    UseCreateCanvasParams,
    UseCreateCanvasResult,
} from '../types';

// Creates a canvas and resolves with the full SerializedCanvas so the caller can
// navigate straight into the new editor. On success every canvas-list query for
// the org is invalidated (the canvas appears under both `mine` and `all`).
const useCreateCanvas = ({ organizationId }: UseCreateCanvasParams): UseCreateCanvasResult => {
    const queryClient = useQueryClient();

    const mutation = useCanvasMutation<CreateCanvasBody, CreateCanvasResponse>({
        organizationId,
        method: 'POST',
        onSuccess: () => {
            invalidateCanvasLists({ queryClient, organizationId });
        },
    });

    const createCanvas = async ({ name }: CreateCanvasInput): Promise<SerializedCanvas> => {
        const response = await mutation.mutateAsync({ path: '/canvases', body: { name } });
        return response.data;
    };

    return {
        createCanvas,
        isCreating: mutation.isLoading,
        error: mutation.error ?? null,
    };
};

export default useCreateCanvas;
